import { useCurrentUser } from '@economic/react-app';
import axios from 'axios';
import { useEffect } from 'react';
import { useFeatureFlags } from '~/app/shared/featureFlags';
import getEnvironment from '~/utilities/getEnvironment';

function getExchangeGuidUrl(): string | undefined {
    switch (getEnvironment()) {
        case 'development':
            return process.env.ECONOMIC_EXCHANGE_GUID_URL;
        case 'production':
            return 'https://www.e-conomic.dk/exchange-guid';
        default:
            // Staging environments
            return undefined;
    }
}

// days * hours * minutes * seconds * milliseconds
const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
const exchangeGuidTimeStampKey = 'exchangeGuidTimeStamp';

function readLastGuidExchangeTimeStamp(): number {
    try {
        const timeStamp = localStorage.getItem(exchangeGuidTimeStampKey);
        const parsedTimeStamp = timeStamp ? parseInt(timeStamp, 10) : 0;
        return Number.isNaN(parsedTimeStamp) ? 0 : parsedTimeStamp;
    } catch (_) {
        return 0;
    }
}

/**
 * @throws Error if localStorage is not supported
 */
function setLastGuidExchangeTimeStamp(timeStamp: number): void {
    localStorage.setItem(exchangeGuidTimeStampKey, timeStamp.toString());
}

export function shouldRenewGuidExchange(timeOfLastExchange: number, timeNow: number): boolean {
    const timeElapsed = timeNow - timeOfLastExchange;
    return timeElapsed >= thirtyDaysInMs;
}

/**
 * This hook is used to share the user guid with e-conomic.dk, and not for a general purpose cross-site tracking.
 */
export function useCrossSiteTracking() {
    const { guid } = useCurrentUser();
    const flags = useFeatureFlags();

    useEffect(() => {
        const exchangeGuidUrl = getExchangeGuidUrl();

        if (!flags.isEnabled('exchangeGuid') || !exchangeGuidUrl) {
            return;
        }

        const now = Date.now();
        const parsedTimeStamp = readLastGuidExchangeTimeStamp();

        if (!shouldRenewGuidExchange(parsedTimeStamp, now)) {
            return;
        }

        try {
            setLastGuidExchangeTimeStamp(now);

            axios.get(`${exchangeGuidUrl}?guid=${guid}`, {
                // withCredentials is required for browser to receive and store the returned cookie
                withCredentials: true,
            });
        } catch (_) {
            // No localstorage support. No exchange to avoid too many requests.
        }
    }, [guid, flags]);
}
